import React, { useEffect, useRef, useState } from "react";

const loadBox = (s) => JSON.parse(sessionStorage.getItem(`gg-box-${s}`) || "[]");
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { BUNDLES } from "@/data/bundles";
import { fetchProductsByCollection } from "@/lib/shopify";
import { useStore } from "@/context/StoreContext";
import { Sparkles, ArrowLeft } from "lucide-react";
import { BundleTile } from "@/components/bundle/BundleTile";
import { BundleStickyBar } from "@/components/bundle/BundleStickyBar";
import { UpgradePopup } from "@/components/bundle/UpgradePopup";
import { HeroBannerSlider, BANNERS } from "@/components/HeroBannerSlider";

const BundlePage = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const bundle = BUNDLES[slug];
  const navigate = useNavigate();
  const { addToCart, setDrawerOpen } = useStore();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState(() => loadBox(slug));
  const loadedSlug = useRef(slug);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeSeen, setUpgradeSeen] = useState(false);
  const [mode, setMode] = useState(slug);
  const [burst, setBurst] = useState(false);

  const active = BUNDLES[mode] || bundle;
  const handlesKey = active?.collectionHandles?.join(",");
  const preselect = params.get("pick");

  // Selection survives a trip to the product page and back
  useEffect(() => {
    setMode(slug);
    if (loadedSlug.current !== slug) {
      loadedSlug.current = slug;
      setSelection(loadBox(slug));
    }
    setUpgradeSeen(sessionStorage.getItem("gg-upgrade-seen") === "1");
  }, [slug]);

  useEffect(() => {
    sessionStorage.setItem(`gg-box-${mode}`, JSON.stringify(selection));
  }, [selection, mode]);

  // Try each mapped Shopify handle until one returns products
  useEffect(() => {
    if (!handlesKey) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      let list = [];
      for (const h of handlesKey.split(",")) {
        list = (await fetchProductsByCollection(h, { first: 60 }).catch(() => [])).filter(Boolean);
        if (list.length) break;
      }
      if (cancelled) return;
      setProducts(list);
      setLoading(false);
      if (preselect && list.some((p) => p.id === preselect)) {
        const cap = BUNDLES[slug]?.qty || 0;
        setSelection((prev) => (prev.includes(preselect) || prev.length >= cap ? prev : [...prev, preselect]));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handlesKey, preselect, slug]);

  // Glam→Dream upgrade popup, once per session
  useEffect(() => {
    if (mode === "glam" && selection.length === 5 && !upgradeSeen) {
      setUpgradeOpen(true);
      setUpgradeSeen(true);
      sessionStorage.setItem("gg-upgrade-seen", "1");
    }
  }, [selection.length, mode, upgradeSeen]);

  // Gold burst when the Dream Box is completed
  useEffect(() => {
    if (mode !== "dream" || selection.length !== 7) return;
    setBurst(true);
    const t = setTimeout(() => setBurst(false), 1800);
    return () => clearTimeout(t);
  }, [selection.length, mode]);

  if (!bundle) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-plum">
        <div className="text-center">
          <h1 className="font-serif text-3xl">Bundle not found</h1>
          <Link to="/" className="mt-4 inline-block text-blush underline">
            Back home
          </Link>
        </div>
      </div>
    );
  }

  const toggle = (pid) =>
    setSelection((prev) => {
      if (prev.includes(pid)) return prev.filter((x) => x !== pid);
      if (prev.length >= active.qty) return prev;
      return [...prev, pid];
    });

  const switchMode = (newMode) => {
    if (newMode === mode || !BUNDLES[newMode]) return;
    setSelection((prev) => prev.slice(0, BUNDLES[newMode].qty));
    setMode(newMode);
  };

  const pickedProducts = selection.map((id) => products.find((p) => p.id === id)).filter(Boolean);
  const complete = selection.length === active.qty;
  const mrpTotal = pickedProducts.reduce((s, p) => s + (p.mrp || p.price || 0), 0);
  const savings = complete ? mrpTotal - active.price : 0;

  const addBundleToCart = () => {
    if (!complete) return;
    pickedProducts.forEach((p) =>
      addToCart(
        {
          ...p,
          bundleId: active.code,
          bundleName: active.name,
          bundlePrice: active.price,
          bundleQty: active.qty,
          bundleHasGift: active.hasFreeGift,
        },
        1,
        true,
      ),
    );
    setDrawerOpen(true);
    setSelection([]);
  };

  return (
    <div className="bg-cream min-h-screen pb-40 sm:pb-28" data-testid={`bundle-page-${slug}`}>
      <HeroBannerSlider
        slides={slug === "it-girl" ? [BANNERS["it-girl"]] : slug === "dream" ? [BANNERS.dream, BANNERS.glam] : [BANNERS.glam, BANNERS.dream]}
        testId="bundle-hero-banner"
      />
      <div className="py-10 lg:py-14" style={{ background: active.softBg }}>
        <div className="max-w-[1200px] mx-auto px-5 lg:px-10">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-plum/70 text-[12px] tracking-[0.22em] uppercase mb-6 hover:text-plum"
            data-testid="bundle-back"
          >
            <ArrowLeft size={14} /> Back
          </button>

          {(mode === "glam" || mode === "dream") && (
            <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="bundle-tabs">
              {[BUNDLES.glam, BUNDLES.dream].map((t) => (
                <button
                  key={t.slug}
                  onClick={() => switchMode(t.slug)}
                  data-testid={`bundle-tab-${t.slug}`}
                  className={`px-4 py-2 rounded-full text-[11px] tracking-[0.22em] uppercase font-semibold transition-[background-color,color,box-shadow] ${
                    mode === t.slug
                      ? "bg-plum text-cream shadow-md"
                      : "bg-cream text-plum/70 border border-plum/15 hover:border-plum/40"
                  }`}
                >
                  {t.qty} pieces · ₹{t.price}
                  {t.hasFreeGift ? " + Gift" : ""}
                </button>
              ))}
            </div>
          )}

          <p className="text-[11px] tracking-[0.4em] uppercase font-semibold" style={{ color: active.accent }}>
            {active.code}
          </p>
          <h1 className="font-serif text-plum leading-[1] mt-2" style={{ fontSize: "clamp(32px, 4.6vw, 60px)" }}>
            {active.name} <span className="italic" style={{ color: active.accent }}>— {active.tagline}</span>
          </h1>
          <p className="mt-3 text-plum/70 font-serif italic max-w-xl">{active.heroCopy}</p>

          {mode === "dream" && selection.length >= 5 && selection.length < 7 && (
            <p
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full font-serif italic text-[13px]"
              style={{ background: "linear-gradient(135deg,#FBEED6,#F6E4C0)", border: "1px solid rgba(197,152,90,0.4)", color: "#4A2C35" }}
              data-testid="bundle-dream-unlock-nudge"
            >
              <Sparkles size={14} className="text-gold" />
              Select {7 - selection.length} more {7 - selection.length === 1 ? "piece" : "pieces"} to unlock your Dream Box + Free Gift
            </p>
          )}

          <p className="mt-5 text-[11px] tracking-[0.22em] uppercase font-semibold text-plum/70">
            Tap <span className="text-plum">+ Add to Box</span> on any {active.qty} pieces · then add your bundle to bag
          </p>
        </div>
      </div>

      <div id="bundle-products" className="max-w-[1200px] mx-auto px-4 sm:px-5 lg:px-10 py-8 lg:py-10 scroll-mt-28">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-pearl animate-pulse rounded-xl" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="text-center py-20 text-plum/60 font-serif italic">No products found in this collection yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-7 sm:gap-x-5">
            {products.map((p) => (
              <BundleTile
                key={p.id}
                product={p}
                active={active}
                picked={selection.includes(p.id)}
                pickOrder={selection.indexOf(p.id) + 1}
                full={selection.length >= active.qty}
                onToggle={toggle}
              />
            ))}
          </div>
        )}
      </div>

      <BundleStickyBar
        active={active}
        pickedProducts={pickedProducts}
        complete={complete}
        savings={savings}
        burst={burst}
        onRemove={toggle}
        onAdd={addBundleToCart}
      />

      {upgradeOpen && (
        <UpgradePopup
          pickedProducts={pickedProducts}
          onYes={() => {
            switchMode("dream");
            setUpgradeOpen(false);
          }}
          onNo={() => setUpgradeOpen(false)}
        />
      )}
    </div>
  );
};

export default BundlePage;
